import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Compass, Hammer, Sparkles, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import MinimalHeader, { goToContact } from "@/components/MinimalHeader";
import MinimalFooter from "@/components/MinimalFooter";

const values = [
  {
    icon: Hammer,
    title: "Tout sur mesure",
    body:
      "Pas de template, pas de thème racheté. Chaque ligne de code, chaque pixel est pensé pour votre activité.",
  },
  {
    icon: Compass,
    title: "Guidés par vos clients",
    body:
      "On part de votre audience — pas de votre concurrence. Un site qui convertit est un site qui parle juste.",
  },
  {
    icon: Sparkles,
    title: "Outils modernes, livraison rapide",
    body:
      "L'IA nous permet de livrer en 2 semaines ce qui prenait 2 mois — sans jamais rogner sur la qualité du rendu.",
  },
  {
    icon: Shield,
    title: "Vous restez propriétaire",
    body:
      "Pas d'abonnement piégeur, pas de plateforme qui vous garde en otage. Le site, le code, le domaine — tout vous appartient.",
  },
];

const Hero = () => (
  <section className="pt-32 pb-16 px-6">
    <div className="max-w-3xl mx-auto text-center">
      <p className="text-sm font-semibold uppercase tracking-widest text-primary mb-6">
        Qui sommes-nous
      </p>
      <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-[1.1] mb-6">
        Un studio <span className="text-primary">indépendant</span>, taillé pour les PME.
      </h1>
      <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
        Lacrea, c'est une petite équipe de créateurs et développeurs qui conçoit
        des sites internet sur mesure pour les entrepreneurs qui veulent être
        pris au sérieux en ligne.
      </p>
    </div>
  </section>
);

const Story = () => (
  <section className="py-16 px-6 border-t border-border/50">
    <div className="max-w-3xl mx-auto">
      <p className="text-xs font-bold uppercase tracking-widest text-primary mb-4">
        Notre histoire
      </p>
      <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-8 leading-tight">
        Des sites comme les grandes marques — pour tout le monde.
      </h2>
      <div className="space-y-5 text-lg text-muted-foreground leading-relaxed">
        <p>
          Nous avons passé des années à construire des sites pour des startups
          et des groupes internationaux. On y a vu les moyens mis en œuvre
          pour qu'un site soit rapide, élégant, bien référencé — et surtout
          qu'il convertisse.
        </p>
        <p>
          Puis, en travaillant avec des artisans, des commerçants, des
          indépendants, on s'est rendu compte qu'ils n'avaient accès qu'à des
          templates génériques ou à des agences qui facturaient 15 000&nbsp;€
          pour un site bancal.
        </p>
        <p>
          Lacrea est né de cette frustration. L'objectif : mettre la qualité
          du web moderne à la portée des entrepreneurs et des PME, avec des
          tarifs clairs, sans abonnement piégeur, et des délais courts.
        </p>
      </div>
    </div>
  </section>
);

const Values = () => (
  <section className="py-20 px-6 border-t border-border/50">
    <div className="max-w-5xl mx-auto">
      <div className="text-center max-w-2xl mx-auto mb-14">
        <p className="text-xs font-bold uppercase tracking-widest text-primary mb-4">
          Ce qui nous anime
        </p>
        <h2 className="text-3xl md:text-4xl font-bold tracking-tight leading-tight">
          Quatre principes, zéro compromis.
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {values.map(({ icon: Icon, title, body }) => (
          <div
            key={title}
            className="p-6 rounded-2xl border border-border bg-card"
          >
            <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center mb-4">
              <Icon className="w-5 h-5 text-primary" strokeWidth={2.25} />
            </div>
            <h3 className="font-semibold text-lg mb-2">{title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Stats = () => (
  <section className="py-16 px-6 border-t border-border/50">
    <div className="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
      <div>
        <p className="text-4xl font-bold text-primary mb-2">2 sem.</p>
        <p className="text-sm text-muted-foreground">Délai de livraison moyen</p>
      </div>
      <div>
        <p className="text-4xl font-bold text-primary mb-2">48h</p>
        <p className="text-sm text-muted-foreground">Réponse à votre demande</p>
      </div>
      <div>
        <p className="text-4xl font-bold text-primary mb-2">100%</p>
        <p className="text-sm text-muted-foreground">Propriété du code livré</p>
      </div>
    </div>
  </section>
);

const CtaBlock = () => {
  const navigate = useNavigate();
  const location = useLocation();
  return (
    <section className="py-20 px-6 border-t border-border/50">
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-5">
          Travaillons ensemble.
        </h2>
        <p className="text-lg text-muted-foreground leading-relaxed mb-8">
          On commence par un appel de 30 minutes — sans engagement, sans jargon.
          Vous nous racontez, on vous dit si on peut aider.
        </p>
        <Button
          onClick={() => goToContact(navigate, location)}
          size="lg"
          className="rounded-full bg-primary hover:bg-primary/90 h-12 px-8 text-base font-semibold shadow-lg shadow-primary/15 group"
        >
          Prendre contact
          <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </Button>
      </div>
    </section>
  );
};

const QuiSommesNous = () => (
  <div className="min-h-screen bg-background text-foreground">
    <SEO
      title="Qui sommes-nous — Studio web sur mesure pour PME"
      description="Lacrea est un studio web indépendant qui conçoit des sites internet sur mesure pour entrepreneurs et PME. Notre approche, nos valeurs, notre histoire."
      path="/qui-sommes-nous"
    />
    <MinimalHeader />
    <main>
      <Hero />
      <Story />
      <Values />
      <Stats />
      <CtaBlock />
    </main>
    <MinimalFooter />
  </div>
);

export default QuiSommesNous;
